import { useState, useEffect, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Button from "../../Common/Button/Button";
import DataTable from "../../Common/DataTable/DataTable";
import { createColumns } from "../../../configuration/tableConfigs";
import IncrementalPagination from "../../Common/DataTable/IncrementalPagination";
import { getAllLoyaltySpecData } from "../../../services/loyaltySpecService";
import customConfig from "../../../config";
import ScreenLoader from "../../Common/ScreenLoader/ScreenLoader";
import {
  DELETE_TYPE_NAME,
  ENABLE_DISABLE_TYPE_NAME,
  //   LOYALTY_SPEC_DESC,
  LOYALTY_SPEC_NAME,
} from "../../../constants/loyaltyConstant";
import AddAndUpdateSpec from "./AddAndUpdateSpec";
import EmptyState from "../../Common/EmptyState/EmptyState";
import { useDesignTokens } from "../../../theme/useDesignTokens";
import ActionsSpec from "./ActionsSpec";
import AppSnackbar from "../../Common/Snackbar/AppSnackbar";
import CenterMessageDialog from "../../Common/Dailog/CenterMsgDailog";

/* Pagination component for rendering regular pagination */
// import Pagination from "../../Common/DataTable/Pagination";

/* Mock Data for development testing */
// import { mockData } from "../../../constants/loyaltyConstant";

const INITIAL_ACTIONS_DATA = {
  type: null,
  data: null,
  open: false,
};
const dispMethod = "snackBar";

const LoyaltySpec = () => {
  const { colors, font } = useDesignTokens();
  const noDataMsg = customConfig?.noDataMsg;
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [orderBy, setOrderBy] = useState(null);
  const [order, setOrder] = useState("asc");
  const [loading, setLoading] = useState(false);
  const [msgToDisplay, setMsgToDisplay] = useState(noDataMsg);
  const [openNewSpec, setOpenNewSpec] = useState(false);
  const [editingSpec, setEditingSpec] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [actionsData, setActionsData] = useState(INITIAL_ACTIONS_DATA);
  const [apiFeedbackMsg, setApiFeedbackMsg] = useState({
    open: false,
    type: "info",
    msg: "",
    data: null,
    dispMethod: dispMethod,
  });

  const fetchSpecs = useCallback(async () => {
    setLoading(true);
    setMsgToDisplay(customConfig.FETCHING_DATA);
    try {
      const response = await getAllLoyaltySpecData({
        page,
        size: rowsPerPage,
      });

      setData(response?.data || []);
      setMsgToDisplay(response?.message);
    } catch (error) {
      setMsgToDisplay(customConfig.FAILED_TO_FETCH);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchSpecs();
  }, [fetchSpecs, refreshKey]);

  // Sorting
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Sort data
  const sortedData = orderBy
    ? [...data].sort((a, b) => {
        const getValue = (obj, path) =>
          path.split(".").reduce((acc, key) => acc?.[key], obj);

        let aValue = orderBy.includes(".") ? getValue(a, orderBy) : a[orderBy];
        let bValue = orderBy.includes(".") ? getValue(b, orderBy) : b[orderBy];

        // Handle null / undefined explicitly
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return order === "asc" ? 1 : -1;
        if (bValue == null) return order === "asc" ? -1 : 1;

        // Normalize strings safely
        const isStringComparison =
          typeof aValue === "string" || typeof bValue === "string";

        if (isStringComparison) {
          aValue = String(aValue).toLowerCase();
          bValue = String(bValue).toLowerCase();
        }

        // Final comparison
        if (aValue < bValue) return order === "asc" ? -1 : 1;
        if (aValue > bValue) return order === "asc" ? 1 : -1;

        return 0;
      })
    : [...data];

  /* For Incremental Pagination */
  const paginatedData = sortedData;

  /* For Regular Pagination */
  //   const paginatedData = sortedData.slice(
  //     page * rowsPerPage,
  //     page * rowsPerPage + rowsPerPage
  //   );

  /* For Regular Pagination */
  //   const handleChangePage = (event, newPage) => {
  //     setPage(newPage);
  //   };
  // const handleChangeRowsPerPage = (event, value) => {
  //     setRowsPerPage(parseInt(value.target.value, 10));
  //     setPage(0);
  //   };

  const handleChangePageForIncrement = (newPage, isLastOrFirst) => {
    if (!isLastOrFirst) {
      setPage(newPage);
    }
  };

  const handleViewConfig = (config) => {};

  const handleEdit = (spec) => {
    setEditingSpec(spec);
    setOpenNewSpec(true);
  };

  const actionsHandler = (row, type) => {
    setActionsData({
      type: type,
      data: row,
      open: true,
    });
  };

  const onCloseFeedback = () => {
    setApiFeedbackMsg((prev) => ({
      ...prev,
      open: false,
      type: "info",
      msg: "",
    }));
  };

  const feedbackHandler = (feedbackData) => {
    // console.log("feedbackData---", feedbackData);
    setApiFeedbackMsg({ ...apiFeedbackMsg, ...feedbackData });
  };

  // Table columns configuration
  const columns = createColumns({
    onViewConfig: handleViewConfig,
    onEdit: handleEdit,
    onToggle: (row) => actionsHandler(row, ENABLE_DISABLE_TYPE_NAME),
    onDelete: (row) => actionsHandler(row, DELETE_TYPE_NAME),
  });

  if (loading) {
    return <ScreenLoader message={msgToDisplay} />;
  } else if (
    typeof msgToDisplay === "string" &&
    typeof customConfig?.PERMISSION_CHECK === "string" &&
    msgToDisplay
      .toLocaleLowerCase()
      .includes(customConfig.PERMISSION_CHECK.toLocaleLowerCase())
  ) {
    return <EmptyState minHeight={"80vh"} message={msgToDisplay} />;
  } else {
    return (
      <>
        <Box sx={{ py: 1, width: "100%", minWidth: 0, boxSizing: "border-box" }}>
          {/* Header Section */}
          <Box
            sx={{
              mb: 1,
              pb: 3,
              borderBottom: "3px solid " + colors?.ghost?.light,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box>
                <Typography
                  variant="h5"
                  sx={{ pt: 1.1, fontWeight: font?.weight?.fontW700, mb: 0.5 }}
                >
                  {LOYALTY_SPEC_NAME}
                </Typography>
                {/* <Typography variant="body2" sx={{ color: "#757575" }}>
                {LOYALTY_SPEC_DESC}
              </Typography> */}
              </Box>
              <Button
                variant="primary"
                size="medium"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingSpec(null);
                  setOpenNewSpec(true);
                }}
                sx={{ px: 3, py: 1.5 }}
              >
                Add New Spec
              </Button>
            </Box>
          </Box>

          {/* Table with sticky header and scrollable body - fluid width for collapsed/expanded sidebar */}
          <Box
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              height: "calc(100vh - 190px)",
              width: "100%",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            {/* Back shadow layer - using Paper for the effect */}
            <Box
              sx={{
                position: "absolute",
                top: "0",
                left: "0",
                right: "0",
                bottom: "0",
                height: "100%",
                backgroundColor: "#F5F5F5",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                zIndex: 0,
                borderRadius: "4px",
              }}
            />
            {/* DataTable Component - with sticky header and scrollable body */}
            <Box
              sx={{
                flex: 1,
                position: "relative",
                zIndex: 1,
                overflow: "hidden",
              }}
            >
              <DataTable
                columns={columns}
                data={paginatedData}
                orderBy={orderBy}
                order={order}
                onSort={handleRequestSort}
                rowKey="action"
                striped
                hoverEffect
                maxHeight="100%"
                msgForDisplay={msgToDisplay}
              />
            </Box>

            {/* Pagination Component - Fixed at bottom */}
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                backgroundColor: "white",
                borderTop: "1px solid #F5F5F5",
                flexShrink: 0,
              }}
            >
              <IncrementalPagination
                page={page}
                pageSize={rowsPerPage}
                dataLength={sortedData.length}
                pageSizeOptions={[5, 10, 25, 50, 100]}
                onPageChange={handleChangePageForIncrement}
                onPageSizeChange={(size) => {
                  setPage(0);
                  setRowsPerPage(size);
                }}
              />
            </Box>
          </Box>
        </Box>

        {openNewSpec && (
          <AddAndUpdateSpec
            open={openNewSpec}
            onClose={(respResult) => {
              setOpenNewSpec(false);
              setEditingSpec(null);
              if (respResult?.success) {
                setPage(0);
                setRefreshKey((prev) => prev + 1);
              }
            }}
            initialData={editingSpec}
            feedbackHandler={feedbackHandler}
          />
        )}

        {actionsData?.open && (
          <ActionsSpec
            open={actionsData?.open}
            onClose={(respResult) => {
              setActionsData(INITIAL_ACTIONS_DATA);
              if (respResult?.success) {
                setRefreshKey((prev) => prev + 1);
              }
            }}
            feedbackHandler={feedbackHandler}
            // feedbackHandler={(feedbackData) => {
            //   setApiFeedbackMsg({ ...apiFeedbackMsg, ...feedbackData });
            // }}
            actionsData={actionsData}
          />
        )}

        {apiFeedbackMsg?.dispMethod === dispMethod
          ? apiFeedbackMsg?.open && (
              <AppSnackbar
                onClose={onCloseFeedback}
                open={apiFeedbackMsg?.open}
                type={apiFeedbackMsg?.type}
                message={apiFeedbackMsg?.msg}
              />
            )
          : apiFeedbackMsg?.open && (
              <CenterMessageDialog
                onClose={onCloseFeedback}
                open={apiFeedbackMsg?.open}
                type={apiFeedbackMsg?.type}
                message={apiFeedbackMsg?.msg}
              />
            )}
      </>
    );
  }
};
export default LoyaltySpec;
